/**
 * How much time a learner gives this, and what that honestly buys them.
 *
 * The projection is the reason this is domain logic rather than copy: it is a claim
 * about outcomes, it is stated to the learner at the moment they choose, and it has to
 * be the same claim everywhere it appears. It was inline in the web onboarding and
 * ported by hand into the Swift and Kotlin onboarding copy — fine for those two, which
 * cannot import this — but the web had no shared home for it, so the settings screen
 * that lets a learner *change* the goal had nowhere to read it from.
 */

/** The choices offered, in minutes. Short options first: 5 minutes is a real answer. */
export const DAILY_GOAL_CHOICES = [5, 10, 15, 30] as const;
export type DailyGoalChoice = (typeof DAILY_GOAL_CHOICES)[number];

/**
 * Months from A2 to B1 at this daily commitment, by our own model.
 *
 * Stated as an estimate wherever it appears, because it is one. An unlisted number
 * falls back to the 10-minute figure rather than interpolating: a precise-looking
 * projection from a number we have not modelled would be a fabrication.
 */
export function monthsToNextLevel(minutes: number): number {
  const table: Readonly<Record<number, number>> = { 5: 14, 10: 7, 15: 5, 30: 3 };
  return table[minutes] ?? 7;
}

/** The sentence the learner reads, identical in onboarding and in settings. */
export function commitmentProjection(minutes: number): string {
  return (
    `${minutes} minutes a day gets most people from A2 to B1 in about ` +
    `${monthsToNextLevel(minutes)} months. That is an estimate, not a promise.`
  );
}
